import React from 'react';
import { bool, func, string, object } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { propTypes } from '../../util/types';
import { PrimaryButton, SecondaryButton } from '../../components';
import { types as sdkTypes } from '../../util/sdkLoader';
import { revokeCustomOffer } from '../../util/api';

import css from './CustomOfferMessage.module.css';

const { Money } = sdkTypes;

/**
 * Helper: Format field key from camelCase/snake_case to readable text
 */
const formatFieldKey = key => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

/**
 * Helper: Check if a field should be displayed
 */
const isDisplayableField = key => {
  const hiddenFields = ['unitType', 'currency'];
  return !hiddenFields.includes(key);
};

/**
 * Helper: Render field value based on type
 */
const renderFieldValue = (intl, key, value, unitType) => {
  if (typeof value === 'boolean') {
    return value ? intl.formatMessage({ id: 'CustomOfferMessage.yes' }) : intl.formatMessage({ id: 'CustomOfferMessage.no' });
  }

  // Translate delivery method values
  if (key === 'deliveryMethod') {
    const deliveryMethodKey = `ProductOrderForm.${value}Option`;
    if (intl.messages[deliveryMethodKey]) {
      return intl.formatMessage({ id: deliveryMethodKey });
    }
    // Fallback: capitalize first letter
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  // For hourly bookings, show full date and time
  if (unitType === 'hour' && (key === 'bookingStart' || key === 'bookingEnd')) {
    if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)) && value.includes('T'))) {
      const date = new Date(value);
      const day = String(date.getDate()).padStart(2, '0');
      const month = intl.formatDate(date, { month: 'short' });
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = date.getHours() >= 12 ? 'PM' : 'AM';

      // Check if German locale
      const isGerman = intl.locale === 'de' || intl.locale?.startsWith('de');
      const connector = isGerman ? 'um' : 'at';
      const timeSuffix = isGerman ? 'Uhr' : ampm;

      return `${day}-${month}-${year} ${connector} ${hours}:${minutes} ${timeSuffix}`;
    }
  }

  // For day/night bookings, show only date
  if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)) && value.includes('T'))) {
    const date = new Date(value);
    const day = String(date.getDate()).padStart(2, '0');
    const month = intl.formatDate(date, { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (typeof value === 'object' && value !== null) {
    return Object.entries(value)
      .map(([k, v]) => `${formatFieldKey(k)}: ${v}`)
      .join(', ');
  }

  return String(value);
};

/**
 * CustomOfferMessage component
 *
 * Displays a custom offer in the activity feed with:
 * - Offer amount
 * - Dynamic offer details (dates, quantity, custom fields, etc.)
 * - Personal message
 * - Package description
 * - Pricing breakdown
 * - Accept/Decline buttons (for customer)
 * - Expiration and staleness warnings
 *
 * @param {Object} props
 * @param {Object} props.message - The message object containing custom offer metadata
 * @param {Object} props.transaction - The transaction object
 * @param {Object} props.currentUser - The current user
 * @param {Function} props.onAcceptOffer - Handler for accepting the offer
 * @param {Function} props.onDeclineOffer - Handler for declining the offer
 * @param {boolean} props.acceptInProgress - Whether accept is in progress
 * @param {boolean} props.declineInProgress - Whether decline is in progress
 * @param {Object} props.acceptError - Error from accepting
 * @param {Object} props.declineError - Error from declining
 * @param {string} props.marketplaceName - Name of the marketplace
 */
const CustomOfferMessage = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    message,
    offer,
    transaction,
    currentUser,
    onAcceptOffer,
    onDeclineOffer,
    onRefreshMessages,
    acceptInProgress,
    declineInProgress,
    acceptError,
    declineError,
  } = props;

  // State for error display and revoke
  const [showUnavailableMessage, setShowUnavailableMessage] = React.useState(false);
  const [unavailableMessage, setUnavailableMessage] = React.useState('');
  const [revokeInProgress, setRevokeInProgress] = React.useState(false);

  // Offer is passed as a prop from ActivityFeed (parsed from message content)
  if (!offer) {
    return null;
  }

  const customOffer = offer;

  // Strip the offer marker from message content for display
  const messageContent = message.attributes.content || '';
  const cleanedMessageContent = messageContent.replace(/\n\n\[CUSTOM_OFFER:.+?\]$/s, '');

  const {
    offerAmount,
    description,
    expiresAt,
    offerStatus = 'pending',
  } = customOffer;

  // Collect offer details for display (excluding null values)
  const offerDetails = {};
  const unitType = customOffer.unitType;

  if (customOffer.quantity) offerDetails.quantity = customOffer.quantity;
  if (customOffer.bookingStart) offerDetails.bookingStart = customOffer.bookingStart;

  // For day bookings, the end date is exclusive (API format)
  // Subtract 1 day to show the inclusive end date to users
  if (customOffer.bookingEnd) {
    const isDaily = unitType === 'day';
    if (isDaily && typeof customOffer.bookingEnd === 'string' && customOffer.bookingEnd.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Parse YYYY-MM-DD format and subtract 1 day
      const [year, month, day] = customOffer.bookingEnd.split('-').map(Number);
      const endDate = new Date(year, month - 1, day); // month is 0-indexed
      endDate.setDate(endDate.getDate() - 1);
      const newYear = endDate.getFullYear();
      const newMonth = String(endDate.getMonth() + 1).padStart(2, '0');
      const newDay = String(endDate.getDate()).padStart(2, '0');
      offerDetails.bookingEnd = `${newYear}-${newMonth}-${newDay}`;
    } else {
      offerDetails.bookingEnd = customOffer.bookingEnd;
    }
  }

  if (customOffer.seats) offerDetails.seats = customOffer.seats;
  if (customOffer.deliveryMethod) offerDetails.deliveryMethod = customOffer.deliveryMethod;

  const isPending = offerStatus === 'pending';
  const isResponded = offerStatus === 'responded';
  const isDeclined = offerStatus === 'declined';
  const isExpired = offerStatus === 'expired';
  const isRevoked = offerStatus === 'revoked';
  const isAutoRevoked = offerStatus === 'auto-revoked';

  // Check if offer is manually expired (legacy check)
  const isManuallyExpired = expiresAt && new Date(expiresAt) < new Date();

  // Check if offer is stale (older than 3 days)
  const createdAt = new Date(message.attributes.createdAt);
  const daysOld = Math.floor((Date.now() - createdAt.getTime()) / (24 * 60 * 60 * 1000));
  const isStale = daysOld >= 3;

  // Determine if current user is provider or customer
  const providerId = transaction.provider?.id?.uuid;
  const customerId = transaction.customer?.id?.uuid;
  const currentUserId = currentUser?.id?.uuid;
  const isProvider = currentUserId === providerId;
  const isCustomer = currentUserId === customerId;

  // Hide revoked/auto-revoked offers from customer
  if ((isRevoked || isAutoRevoked) && isCustomer) {
    return null;
  }

  // Hide status-only messages since we already show status in the badge
  // Only show actual provider messages
  const statusOnlyMessages = [
    'Customer responded.',
    'Customer declined the offer.',
    'You responded.',
    'You declined the offer.'
  ];

  const displayMessageContent = statusOnlyMessages.includes(cleanedMessageContent.trim())
    ? ''
    : cleanedMessageContent;

  // Handle accept with error display
  const handleAcceptOffer = () => {
    setShowUnavailableMessage(false); // Clear any previous error

    onAcceptOffer({ inquiryTransactionId: transaction.id, offerId: customOffer.id })
      .catch(error => {
        console.log('🔍 Accept error caught in CustomOfferMessage:', error);
        // Check if it's an unavailability/expiry error
        if (error?.data?.offerExpired || error?.data?.offerAutoRevoked) {
          // Show alert toast
          alert('This offer is no longer valid. You need to contact the provider.');

          // Refresh messages to show updated offer status
          if (onRefreshMessages) {
            onRefreshMessages();
          }

          // Don't re-throw for friendly errors - they are handled here
          return;
        }
        // For other errors, re-throw so parent component can handle
        throw error;
      });
  };

  // Handle manual revoke by provider
  const handleRevokeOffer = () => {
    setRevokeInProgress(true);

    revokeCustomOffer({
      transactionId: transaction.id,
      offerId: customOffer.id
    })
      .then(() => {
        console.log('✅ Offer revoked successfully');
        setRevokeInProgress(false);
        // Refresh messages to show updated offer status
        if (onRefreshMessages) {
          onRefreshMessages();
        }
      })
      .catch(error => {
        console.error('❌ Revoke error:', error);
        setRevokeInProgress(false);
      });
  };

  const classes = classNames(rootClassName || css.root, className, {
    [css.pending]: isPending,
    [css.responded]: isResponded,
    [css.declined]: isDeclined,
    [css.expired]: isExpired,
  });

  return (
    <div className={classes}>
      <div className={css.header}>
        <h4 className={css.title}>
          <FormattedMessage id="CustomOfferMessage.title" />
        </h4>
        {isExpired && (
          <span className={css.statusBadge}>
            <FormattedMessage id="CustomOfferMessage.expired" />
          </span>
        )}
        {!isExpired && isAutoRevoked && (
          <span className={classNames(css.statusBadge, css.autoRevokedBadge)}>
            <FormattedMessage id="CustomOfferMessage.autoRevoked" />
          </span>
        )}
        {!isExpired && isRevoked && (
          <span className={classNames(css.statusBadge, css.revokedBadge)}>
            <FormattedMessage id="CustomOfferMessage.revoked" />
          </span>
        )}
        {!isExpired && !isAutoRevoked && !isRevoked && isPending && (
          <span className={classNames(css.statusBadge, css.pendingBadge)}>
            <FormattedMessage id="CustomOfferMessage.pending" />
          </span>
        )}
        {!isExpired && !isAutoRevoked && !isRevoked && isResponded && (
          <span className={classNames(css.statusBadge, css.respondedBadge)}>
            <FormattedMessage id="CustomOfferMessage.responded" />
          </span>
        )}
        {!isExpired && !isAutoRevoked && !isRevoked && isDeclined && (
          <span className={classNames(css.statusBadge, css.declinedBadge)}>
            <FormattedMessage id="CustomOfferMessage.declined" />
          </span>
        )}
      </div>

      <div className={css.offerAmount}>
        <span className={css.amountLabel}>
          <FormattedMessage id="CustomOfferMessage.offerAmount" />
        </span>
        <span className={css.amountValue}>
          {formatMoney(intl, new Money(offerAmount, customOffer.currency))}
        </span>
      </div>

      {/* Personal Message */}
      {displayMessageContent && (
        <div className={css.messageContent}>
          <p>{displayMessageContent}</p>
        </div>
      )}

      {/* Package Description */}
      {description && (
        <div className={css.packageDescription}>
          <h5 className={css.sectionTitle}>
            <FormattedMessage id="CustomOfferMessage.packageDescription" />
          </h5>
          <p>{description}</p>
        </div>
      )}

      {/* Dynamic Offer Details */}
      {offerDetails && Object.keys(offerDetails).length > 0 && (
        <div className={css.offerDetails}>
          <h5 className={css.sectionTitle}>
            <FormattedMessage id="CustomOfferMessage.offerDetails" />
          </h5>
          <dl className={css.detailsList}>
            {Object.entries(offerDetails)
              .filter(([key]) => isDisplayableField(key))
              .map(([key, value]) => {
                // Get translated label for the field
                let displayLabel = formatFieldKey(key);

                // For hourly bookings, use specific time labels
                if (unitType === 'hour') {
                  if (key === 'bookingStart') displayLabel = intl.formatMessage({ id: 'CustomOfferMessage.startTime' });
                  if (key === 'bookingEnd') displayLabel = intl.formatMessage({ id: 'CustomOfferMessage.endTime' });
                }
                // For other fields, check if we have a translation key
                else if (intl.messages[`CustomOfferMessage.${key}`]) {
                  displayLabel = intl.formatMessage({ id: `CustomOfferMessage.${key}` });
                }

                return (
                  <div key={key} className={css.detailItem}>
                    <dt className={css.detailLabel}>
                      {displayLabel}:
                    </dt>
                    <dd className={css.detailValue}>
                      {renderFieldValue(intl, key, value, unitType)}
                    </dd>
                  </div>
                );
              })}
          </dl>
        </div>
      )}

      {/* Pricing Breakdown - removed since line items are calculated at acceptance time */}

      {/* Staleness Warning */}
      {isPending && isStale && isCustomer && !isExpired && !isManuallyExpired && (
        <div className={css.stalenessWarning}>
          <FormattedMessage
            id="CustomOfferMessage.stalenessWarning"
            values={{ days: daysOld }}
          />
        </div>
      )}

      {/* Expiration Info */}
      {expiresAt && !isExpired && !isManuallyExpired && isPending && (
        <div className={css.expirationInfo}>
          <FormattedMessage
            id="CustomOfferMessage.expiresOn"
            values={{
              date: intl.formatDate(new Date(expiresAt), {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              }),
            }}
          />
        </div>
      )}

      {/* Expired Offer Message */}
      {(isExpired || isManuallyExpired) && (
        <div className={css.expiredMessage}>
          <div className={css.expiredIcon}>⚠️</div>
          <div className={css.expiredText}>
            <FormattedMessage id="CustomOfferMessage.expiredOfferMessage" />
          </div>
        </div>
      )}

      {/* Friendly unavailability message - shown when accept fails */}
      {showUnavailableMessage && (
        <div className={css.unavailableMessage}>
          <div className={css.unavailableIcon}>ℹ️</div>
          <div className={css.unavailableText}>
            {unavailableMessage}
          </div>
        </div>
      )}

      {/* Customer: Accept/Decline Buttons (only for pending, non-expired offers) */}
      {isCustomer && isPending && !isExpired && !isManuallyExpired && (
        <>
          <div className={css.actionWarning}>
            <FormattedMessage id="CustomOfferMessage.actionWarning" />
          </div>
          <div className={css.actionButtons}>
            <PrimaryButton
              onClick={handleAcceptOffer}
              inProgress={acceptInProgress}
              disabled={acceptInProgress || declineInProgress}
            >
              <FormattedMessage id="CustomOfferMessage.acceptButton" />
            </PrimaryButton>
            <SecondaryButton
              onClick={() => {
                console.log('🔍 Decline params:', {
                  transactionId: transaction.id,
                  transactionIdUuid: transaction.id?.uuid,
                  offerId: customOffer.id
                });
                onDeclineOffer({ transactionId: transaction.id, offerId: customOffer.id });
              }}
              inProgress={declineInProgress}
              disabled={acceptInProgress || declineInProgress}
            >
              <FormattedMessage id="CustomOfferMessage.declineButton" />
            </SecondaryButton>
          </div>
        </>
      )}

      {/* Provider: Revoke Button (only for pending, non-expired offers) */}
      {isProvider && isPending && !isExpired && !isManuallyExpired && (
        <div className={css.providerActions}>
          <SecondaryButton
            onClick={handleRevokeOffer}
            inProgress={revokeInProgress}
            disabled={revokeInProgress}
          >
            <FormattedMessage id="CustomOfferMessage.revokeButton" />
          </SecondaryButton>
        </div>
      )}

      {/* Errors */}
      {acceptError && (
        <div className={css.error}>
          <FormattedMessage id="CustomOfferMessage.acceptError" />
          {acceptError.message && <p>{acceptError.message}</p>}
        </div>
      )}
      {declineError && (
        <div className={css.error}>
          <FormattedMessage id="CustomOfferMessage.declineError" />
        </div>
      )}
    </div>
  );
};

CustomOfferMessage.defaultProps = {
  rootClassName: null,
  className: null,
  acceptInProgress: false,
  declineInProgress: false,
  acceptError: null,
  declineError: null,
};

CustomOfferMessage.propTypes = {
  rootClassName: string,
  className: string,
  message: propTypes.message.isRequired,
  offer: object.isRequired,
  transaction: propTypes.transaction.isRequired,
  currentUser: propTypes.currentUser.isRequired,
  onAcceptOffer: func.isRequired,
  onDeclineOffer: func.isRequired,
  acceptInProgress: bool,
  declineInProgress: bool,
  acceptError: propTypes.error,
  declineError: propTypes.error,
};

export default CustomOfferMessage;
